import jwtDecode from 'jwt-decode';
import { loginPubClient, tensaiPrivClient } from './api';
import AuthService from './auth';

export default class TensaiService {
  constructor() {
    this.authService = new AuthService();
  }

  /**
   * [private]
   * verifica se existe uma determinada permissão no token
   */
  checkPerm(token, perm) {
    const decoded = jwtDecode(token);
    return decoded.sub_permissions.includes(perm);
  }

  /**
   * [private]
   * extrai o token do cabeçalho, caso exista
   * @param {*} response
   */
  extractTokenFromResponse(response) {
    const token = response.headers.authorization.split(' ');
    return Array.isArray(token) ? token[1] : null;
  }

  /**
   * extrai o token do cabeçalho de trocar senha
   * @param {*} response
   */
  extractChangePasswordTokenFromResponse(response) {
    const token = response.headers['change-password-token'];
    return token ? token : null;
  }

  /**
   * [private]
   * recupera os dados de usuário do token
   */
  getData(token) {
    if (token) {
      const decoded = jwtDecode(token);
      return {
        token,
        id: decoded.id,
        authenticated: true,
        name: decoded.sub_name,
        photo: decoded.sub_photo,
        isAdmin: decoded.sub_tipo_usuario.includes('ADMINISTRADOR'),
        isEmployee: decoded.sub_tipo_usuario.includes('FUNCIONARIO'),
        isCustomer: decoded.sub_tipo_usuario.includes('CLIENTE'),
        expireIn: decoded.exp,
        tenant: null,
        needChangePassword: decoded.need_change_password,
        needSignDocuments: decoded.need_sign_documents,
      };
    }
    // default
    return {
      authenticated: false,
    };
  }

  /**
   * solicita a criação de um acesso
   */
  createAccess(authData) {
    return new Promise((resolve, reject) => {
      loginPubClient
        .post('/security/login', authData)
        .then((result) => {
          // extract token
          const token = this.extractTokenFromResponse(result);
          const changeToken = this.extractChangePasswordTokenFromResponse(result)
          // if token not exists, reject
          if (!token) {
            reject(new Error('Token not found'));
          }
          if (changeToken && token) {
            this.authService.setChangePasswordToken(changeToken)
          }
          // set token in localStorage
          this.authService.setToken(token, () => {
            const data = this.getData(token);
            resolve({ data });
          });
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  /**
   * update session
   */
  updateSession(authData, token = null) {
    return new Promise((resolve, reject) => {
      loginPubClient
        .post('/redefinir-senha/alterar?login=true', {
          token: token || this.authService.getChangePasswordToken(),
          senha: authData.password,
        })
        .then((result) => {
          // extract token
          const token = this.extractTokenFromResponse(result);
          // if token not exists, reject
          if (!token) {
            reject(new Error('Token not found'));
          }
          // set token in localStorage
          this.authService.setToken(token, () => {
            this.authService.removeChangePasswordToken()
            const data = this.getData(token);
            resolve({ data });
          });
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  /**
   * solicita o cancelamento de acesso
   */
  destroyAccess() {
    // eslint-disable-next-line no-unused-vars
    return new Promise((resolve, reject) => {
      this.authService.removeToken(() => {
        const data = this.getData(null);
        resolve({ data });
      });
    });
  }

  /**
   * recupera o token corrente
   */
  getCurrentAccess() {
    const token = this.authService.getToken();
    return new Promise((resolve, reject) => {
      if (token) {
        const data = this.getData(token);
        resolve({ data });
      }
      reject(new Error('Token is undefined'));
    });
  }

  /**
   * recupera uma lista de clientes baseado na autenticação
   */
  fetchTenants() {
    const result = tensaiPrivClient.get('/clientes/analyze/?ativo=true');
    return result;
  }

  /**
   * Recupera os documentos lgpd
   */
  getLgpdDocuments() {
    const result = tensaiPrivClient.get('/configurations/lgpd/documents');
    return result;
  }

  /**
   * Recupera os documentos LGPD pendentes de assinatura do usuário autenticado.
   * Retorna IDocumentInfo[] (id, location, type, ...).
   */
  getToSignLgpdDocuments() {
    return tensaiPrivClient.get('/configurations/lgpd/documents/sign');
  }

  /**
   * Assina os documentos LGPD informados.
   * O `refreshToken=true` vai como query param; o backend responde com o header
   * `Refreshed-Token` contendo o JWT novo (já com need_sign_documents=false),
   * aplicado pelo interceptor de resposta em api.js.
   * @param {number[]} ids
   */
  signLgpdDocuments(ids) {
    return tensaiPrivClient.post('/configurations/lgpd/documents/sign', ids, {
      params: { refreshToken: true },
    });
  }

  checkValidateOfCurrentSession() {
    const { data } = this.getCurrentAccess();
    //
    const dCur = new Date();
    const dExp = new Date(data.expireIn);
    //
    return dCur.getTime() < dExp.getTime();
  }

  /**
   * Solicita a definição de uma nova senha, um email será enviado ao email passado por parametro
   * para a atualização da senha
   * @param {*} userEmail
   */
  sendRecoverPasswordEmail(userEmail) {
    const result = tensaiPrivClient.post('/redefinir-senha', { login: userEmail, callbackLink: process.env.REACT_APP_RECOVERY_PASSWORD, solicitante: "analyze" });
    return result;
  }

  /**
   * Envia o token e a nova senha para o servidor para que a senha do usuário seja atualizada.
   * @param {*} incommingToken
   * @param {*} newPassword
   */
  updatePassword(incommingToken, newPassword) {
    const result = tensaiPrivClient.post('/redefinir-senha/alterar', { token: incommingToken, nova_senha: newPassword });
    return result;
  }

  isTokenValid(incommingToken) {
    const result = tensaiPrivClient.post(`/redefinir-senha/token/valid?token=${incommingToken}`);
    return result;
  }

  /**
   * Request utilizada na feature de troca de senha
   * Foi criada para manter uma paridade com o fluxo que ocorre no tensai
   */
  verifyTokenForPasswordChange(incommingToken) {
    return tensaiPrivClient.post('/redefinir-senha/token/valid', { token: incommingToken })
      .then(response => response.status);
  }
}
